import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { getBrandRegionOptions, getRegionLabel } from '../../shared/utils/brandRegions';
import {
  isBrandPhoneValid,
  normalizeBrandPhone,
} from '../../shared/utils/brandCountry';
import { formatCrmPhone, getCrmPhonePlaceholder, getCrmPhoneTelHref } from './utils/crmSaudiFormat';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserCheck, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, AlertCircle,
  Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, FileText,
  Filter, Eye, X, XCircle, LayoutGrid, Layers, Users, MapPin, TrendingUp,
  DollarSign, Target, Award, Phone, Mail,
} from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters } from '../../shared/utils/columnHeaderFilters';
import DatePicker, { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import localApiAdapter from '../../core/services/localApiAdapter';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import QawaemErpListPageShell from '../../products/saudi/components/QawaemErpListPageShell';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import {
  CrmModalFormTabs,
  CrmModalFormBody,
  CrmModalFormPanel,
  CrmModalFieldGrid,
} from './components/CrmModalFormTabs';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import { QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { enrichStatusOptions, resolveCrmModalTabs } from '../../shared/utils/crmPageUtils';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { mapStatsForListShell, buildListImportExportProps } from '../../shared/utils/erpListPageShellHelpers';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { dispatchCrmApiRefresh, CRM_SALES_AGENTS_REFRESH_EVENT } from './crmRefreshEvents';
import { useCrmApiRefreshListener } from './crmPickerOptions';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useCrmConfig } from './useCrmConfig';
import { sliceCrmListItems } from './crmListPageUtils';
import '../../core/styles/global-dropdowns.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

const AGENT_TABLE_COLUMNS = [
  { key: 'code', labelKey: 'col_code' },
  { key: 'name', labelKey: 'col_agent' },
  { key: 'territory', labelKey: 'col_territory' },
  { key: 'total_sales', labelKey: 'col_total_sales' },
  { key: 'conversion_rate', labelKey: 'col_conversion_rate' },
  { key: 'performance', labelKey: 'col_performance', align: 'center' },
  { key: 'status', labelKey: 'col_status', align: 'center' },
];

registerLocale('fr', fr);
registerLocale('ar', arLocale);

const AGENT_FORM_ID = 'crm-sales-agent-form';
const AGENT_MODAL_FIELD_COUNT = 21;

const SalesAgentsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.crm.pages.salesAgentsPage.${key}`, opts);
  const cfgTheme = useModuleConfigTheme();
  const crmConfig = useCrmConfig();
  const isLight = cfgTheme.light;
  const viewPanel = isLight ? 'rounded-xl p-5 border border-slate-200 bg-slate-50' : 'bg-white/5 rounded-xl p-5 border border-white/20';
  const viewLabel = isLight ? 'text-slate-500 text-sm mb-1' : 'text-white/60 text-sm mb-1';
  const viewValue = isLight ? 'text-slate-900 font-medium' : 'text-white font-medium';
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const regionOptions = useMemo(() => getBrandRegionOptions(), []);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(dateLocale) : '-');
  const fmtMoney = (n) => formatBrandMoney(n);
  const phonePlaceholder = getCrmPhonePlaceholder();
  const [searchTerm, setSearchTerm] = useState('');
  const [agents, setAgents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [formTab, setFormTab] = useState('agent');

  const agentFormTabs = useMemo(() => resolveCrmModalTabs(AGENT_MODAL_FIELD_COUNT, [
    { id: 'agent', label: p('section_agent_info'), icon: UserCheck, iconClassName: 'text-emerald-400' },
    { id: 'territory', label: p('section_territory_quota'), icon: Target, iconClassName: 'text-blue-400' },
    { id: 'performance', label: p('section_performance'), icon: TrendingUp, iconClassName: 'text-green-400' },
  ]), [p]);
  const agentDefaultTab = agentFormTabs[0]?.id || 'agent';

  const [formData, setFormData] = useState({
    agent_id: '',
    code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    city: '',
    territory: '',
    region: '',
    company: '',
    hire_date: '',
    sales_quota: '',
    commission_rate: '',
    total_sales: '',
    active_leads: '',
    closed_deals: '',
    conversion_rate: '',
    total_commission: '',
    status: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_agents');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: entityLabel });

  useEffect(() => { loadAgents(); }, []);
  useCrmApiRefreshListener(CRM_SALES_AGENTS_REFRESH_EVENT, loadAgents);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAgents = async () => {
    try {
      const response = await localApiAdapter.get('/crm/sales-agents');
      setAgents(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading sales agents:', error);
      setAgents([]);
    }
  };

  const generateAgentCode = () => {
    const year = new Date().getFullYear();
    const count = agents.length + 1;
    const paddedCount = String(count).padStart(3, '0');
    return `AG-${year}-${paddedCount}`;
  };

  const openNewAgentModal = () => {
    setEditingRecord(null);
    setFormData({ 
      agent_id: '', code: generateAgentCode(), first_name: '', last_name: '', email: '', phone: '', mobile: '',
      address: '', city: '', territory: '', region: '', company: '', hire_date: '',
      sales_quota: '', commission_rate: '', total_sales: '', active_leads: '', closed_deals: '',
      conversion_rate: '', total_commission: '', status: '', notes: ''
    });
    setFormTab(agentDefaultTab);
    setShowModal(true);
  };

  const stats = {
    total: agents.length,
    active: agents.filter(a => a.status === 'active').length,
    territories: [...new Set(agents.map(a => a.territory).filter(Boolean))].length,
    totalSales: agents.reduce((sum, a) => sum + (parseFloat(a.total_sales) || 0), 0),
    totalCommission: agents.reduce((sum, a) => sum + (parseFloat(a.total_commission) || 0), 0),
  };

  const mainTabs = [
    { id: 'all_agents', name: p('mainTab_allAgents'), icon: LayoutGrid },
    { id: 'par_territoire', name: p('mainTab_byTerritory'), icon: MapPin },
    { id: 'par_performance', name: p('mainTab_byPerformance'), icon: TrendingUp }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('quickFilter_all'), icon: Layers },
    { id: 'active', name: p('quickFilter_active'), icon: CheckCircle },
    { id: 'top_performers', name: p('quickFilter_topPerformers'), icon: Award },
    { id: 'needs_support', name: p('quickFilter_needsSupport'), icon: AlertCircle },
    { id: 'new_agents', name: p('quickFilter_newAgents'), icon: Users }
  ];

  const statusDistributionData = [
    { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
    { filterKey: 'inactive', name: p('dist_inactive'), count: agents.filter(a => a.status === 'inactive').length, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
    { filterKey: 'training', name: p('dist_training'), count: agents.filter(a => a.status === 'training').length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
    { filterKey: 'top_performers', name: p('dist_topPerformers'), count: agents.filter(a => (parseFloat(a.conversion_rate) || 0) > 30).length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' }
  ];

  const performanceDistributionData = [
    { filterKey: 'perf_excellent', name: p('dist_perf_excellent'), count: agents.filter(a => (parseFloat(a.conversion_rate) || 0) > 80).length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
    { filterKey: 'perf_good', name: p('dist_perf_good'), count: agents.filter(a => { const rate = parseFloat(a.conversion_rate) || 0; return rate >= 50 && rate <= 80; }).length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
    { filterKey: 'perf_average', name: p('dist_perf_average'), count: agents.filter(a => { const rate = parseFloat(a.conversion_rate) || 0; return rate >= 20 && rate < 50; }).length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
    { filterKey: 'perf_low', name: p('dist_perf_low'), count: agents.filter(a => (parseFloat(a.conversion_rate) || 0) < 20).length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
  ];

  const agentDistribution = {
    all_agents: {
      title: p('dist_allAgentsTitle'),
      data: statusDistributionData
    },
    par_territoire: {
      title: p('dist_byTerritoryTitle'),
      data: (() => {
        const territories = {};
        agents.forEach(a => {
          const territory = a.territory || '__undefined__';
          territories[territory] = (territories[territory] || 0) + 1;
        });
        return Object.entries(territories).slice(0, 4).map(([filterKey, count]) => ({
          filterKey,
          name: filterKey === '__undefined__' ? p('undefined_territory') : getRegionLabel(filterKey) || filterKey,
          count,
          color: 'bg-gradient-to-r from-indigo-500 to-indigo-600'
        }));
      })()
    },
    par_performance: {
      title: p('dist_byPerformanceTitle'),
      data: performanceDistributionData
    }
  };

  const getDistributionFilterId = (item) => item.filterKey || item.name;

  const distributionInfo = agentDistribution[mainViewMode] || agentDistribution.all_agents;

  const getPerformanceTier = (rate) => {
    const numRate = parseFloat(rate) || 0;
    if (numRate > 80) return 'excellent';
    if (numRate >= 50) return 'good';
    if (numRate >= 20) return 'average';
    return 'low';
  };

  const baseFilteredAgents = useMemo(() => {
    return agents.filter((a) => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (mainViewMode === 'all_agents') {
        if (quickFilter === 'active' && a.status !== 'active') return false;
        if (quickFilter === 'top_performers' && (parseFloat(a.conversion_rate) || 0) <= 30) return false;
        if (quickFilter === 'needs_support' && (parseFloat(a.conversion_rate) || 0) >= 20) return false;
        if (quickFilter === 'new_agents') {
          const hireDate = new Date(a.hire_date);
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          if (hireDate < threeMonthsAgo) return false;
        }
      }
      if (activeDistributionFilter) {
        switch (mainViewMode) {
          case 'all_agents':
            if (activeDistributionFilter === 'active' && a.status !== 'active') return false;
            if (activeDistributionFilter === 'inactive' && a.status !== 'inactive') return false;
            if (activeDistributionFilter === 'training' && a.status !== 'training') return false;
            if (activeDistributionFilter === 'top_performers' && (parseFloat(a.conversion_rate) || 0) <= 30) return false;
            break;
          case 'par_territoire': {
            const territoryKey = a.territory || '__undefined__';
            if (activeDistributionFilter && territoryKey !== activeDistributionFilter) return false;
            break;
          }
          case 'par_performance': {
            const rate = parseFloat(a.conversion_rate) || 0;
            if (activeDistributionFilter === 'perf_excellent' && rate <= 80) return false;
            if (activeDistributionFilter === 'perf_good' && (rate < 50 || rate > 80)) return false;
            if (activeDistributionFilter === 'perf_average' && (rate < 20 || rate >= 50)) return false;
            if (activeDistributionFilter === 'perf_low' && rate >= 20) return false;
            break;
          }
          default:
            break;
        }
      }
      return true;
    });
  }, [agents, statusFilter, searchTerm, viewMode, mainViewMode, quickFilter, activeDistributionFilter]);

  const agentColumnRules = useMemo(
    () => [
      { key: 'code', getValue: (a) => String(a.code ?? '') },
      {
        key: 'name',
        getValue: (a) => `${a.first_name ?? ''} ${a.last_name ?? ''} ${a.email ?? ''}`.trim(),
      },
      {
        key: 'territory',
        getValue: (a) => getRegionLabel(a.territory) || String(a.territory ?? ''),
      },
      { key: 'total_sales', getValue: (a) => String(parseFloat(a.total_sales) || 0) },
      { key: 'conversion_rate', getValue: (a) => String(a.conversion_rate ?? '') },
      { key: 'performance', getValue: (a) => getPerformanceTier(a.conversion_rate), mode: 'exact' },
      { key: 'status', getValue: (a) => String(a.status ?? ''), mode: 'exact' },
    ],
    [],
  );

  const filteredAgents = useMemo(
    () => applyColumnFilters(baseFilteredAgents, colFilters, agentColumnRules),
    [baseFilteredAgents, colFilters, agentColumnRules],
  );

  const pagedFilteredAgents = useMemo(
    () => sliceCrmListItems(filteredAgents, crmConfig.itemsPerPage),
    [filteredAgents, crmConfig.itemsPerPage],
  );

  const AGENT_STATUS_OPTIONS = [
    { value: 'active', label: p('status_active') },
    { value: 'inactive', label: p('status_inactive') },
    { value: 'training', label: p('status_training') },
    { value: 'suspended', label: p('status_suspended') }
  ];

  const normalizeAgentStatus = (raw) => {
    if (raw == null || raw === '') return '';
    const s = String(raw).trim();
    if (AGENT_STATUS_OPTIONS.some((o) => o.value === s)) return s;
    const byLabel = AGENT_STATUS_OPTIONS.find(
      (o) => o.label.toLowerCase() === s.toLowerCase()
    );
    return byLabel ? byLabel.value : '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const statusRaw = formData.status;
    const statusNorm = normalizeAgentStatus(statusRaw);
    if (!statusNorm) {
      alert(p('alert_select_status'));
      return;
    }
    const phone = normalizeBrandPhone(formData.phone);
    const mobile = normalizeBrandPhone(formData.mobile);
    if ((formData.phone && !isBrandPhoneValid(formData.phone)) || (formData.mobile && !isBrandPhoneValid(formData.mobile))) {
      alert(p('alert_invalid_phone'));
      return;
    }
    try {
      const submitData = { ...formData, status: statusNorm, phone, mobile };
      if (editingRecord) {
        await localApiAdapter.put(`/crm/sales-agents/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/crm/sales-agents', submitData);
        notifyCreated(showNotify, entityLabel);
      }
      dispatchCrmApiRefresh('/crm/sales-agents');
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error'));
      return;
    } finally {
      resetForm();
      loadAgents();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setFormTab(agentDefaultTab);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/crm/sales-agents/${id}`);
        dispatchCrmApiRefresh('/crm/sales-agents');
        notifyDeleted(showNotify, entityLabel);
        loadAgents();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error'));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/crm/sales-agents/${id}`)));
        dispatchCrmApiRefresh('/crm/sales-agents');
        notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
        setSelectedRecords([]);
        loadAgents();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error'));
      }
    }
  };

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredAgents);

  useEffect(() => {
    if (selectedRecords.length > 0) setEditingKey(null);
  }, [selectedRecords, setEditingKey]);

  const resetForm = () => {
    setFormData({ 
      agent_id: '', code: '', first_name: '', last_name: '', email: '', phone: '', mobile: '',
      address: '', city: '', territory: '', region: '', company: '', hire_date: '',
      sales_quota: '', commission_rate: '', total_sales: '', active_leads: '', closed_deals: '',
      conversion_rate: '', total_commission: '', status: '', notes: ''
    });
    setEditingRecord(null);
    setFormTab(agentDefaultTab);
    setShowModal(false);
  };

  const statusOptions = useMemo(() => enrichStatusOptions([
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'inactive', label: p('status_inactive'), icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
    { value: 'training', label: p('status_training'), icon: AlertCircle, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'suspended', label: p('status_suspended'), icon: AlertTriangle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ]), [p]);

  const handleStatusChange = async (recordId, newStatus) => {
    const agent = agents.find((a) => a.id === recordId);
    if (!agent) return;
    try {
      await localApiAdapter.put(`/crm/sales-agents/${recordId}`, { ...agent, status: newStatus });
      dispatchCrmApiRefresh('/crm/sales-agents');
      loadAgents();
    } catch (error) {
      console.error('Error updating status:', error);
      alert(p('save_error'));
    }
  };

  const agentHeaderFilterKind = (key) => {
    if (key === 'status' || key === 'performance') return 'select';
    return 'text';
  };

  const agentHeaderFilterOptions = (key) => {
    if (key === 'status') return AGENT_STATUS_OPTIONS;
    if (key === 'performance') {
      return [
        { value: 'excellent', label: p('perf_excellent') },
        { value: 'good', label: p('perf_good') },
        { value: 'average', label: p('perf_average') },
        { value: 'low', label: p('perf_low') },
      ];
    }
    return [];
  };

  const getPerformanceBadge = (rate) => {
    const numRate = parseFloat(rate) || 0;
    if (numRate > 80) return <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-green-500/20 text-green-400 border-green-500/30"><Award size={14} />{p('perf_excellent')}</span>;
    if (numRate >= 50) return <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-blue-500/20 text-blue-400 border-blue-500/30"><TrendingUp size={14} />{p('perf_good')}</span>;
    if (numRate >= 20) return <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Target size={14} />{p('perf_average')}</span>;
    return <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-red-500/20 text-red-400 border-red-500/30"><AlertCircle size={14} />{p('perf_low')}</span>;
  };

  const qawaem = isSaudiBrand();
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(AGENT_TABLE_COLUMNS.map((c) => [c.key, true]))
  );

  const qawaemColumnDefs = useMemo(
    () => AGENT_TABLE_COLUMNS.map((col) => ({
      key: col.key,
      label: p(col.labelKey),
      checked: columnVisibility[col.key] !== false,
    })),
    [columnVisibility, p],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const visibleColumns = useMemo(
    () => AGENT_TABLE_COLUMNS.filter((c) => columnVisibility[c.key] !== false),
    [columnVisibility],
  );
  const tableColSpan = visibleColumns.length + 2;
  const listPageStats = mapStatsForListShell([
    { label: p('stat_total_agents'), value: stats.total, icon: Users },
    { label: p('stat_active'), value: stats.active, icon: CheckCircle },
    { label: p('stat_territories'), value: stats.territories, icon: MapPin },
    { label: p('stat_total_sales'), value: fmtMoney(stats.totalSales), icon: DollarSign },
  ]);

  const renderAgentHeader = (col) => (
    <TableColumnHeaderFilter
      key={col.key}
      columnKey={col.key}
      label={p(col.labelKey)}
      kind={agentHeaderFilterKind(col.key)}
      value={colFilters[col.key] ?? ''}
      onChange={(v) => setColFilter(col.key, v)}
      options={agentHeaderFilterOptions(col.key)}
      editingKey={editingKey}
      onEditingKeyChange={setEditingKey}
      align={col.align === 'center' ? 'center' : undefined}
      className={col.align === 'center' ? 'text-center' : undefined}
      thClassName="text-white py-4"
    />
  );

  const renderAgentCell = (record, col) => {
    switch (col.key) {
      case 'code':
        return <td key={col.key} className="px-6 py-4"><span className="text-white font-semibold">{record.code}</span></td>;
      case 'name':
        return (
          <td key={col.key} className="px-6 py-4">
            <div className="flex flex-col">
              <span className="text-white font-semibold">{record.first_name} {record.last_name}</span>
              <span className="text-white/50 text-xs mt-0.5">{record.email}</span>
            </div>
          </td>
        );
      case 'territory':
        return (
          <td key={col.key} className="px-6 py-4">
            <div className="flex items-center gap-1.5 text-white/70">
              <MapPin size={14} />
              {getRegionLabel(record.territory) || record.territory || p('dash')}
            </div>
          </td>
        );
      case 'total_sales':
        return (
          <td key={col.key} className="px-6 py-4">
            <div className="flex flex-col">
              <span className="text-white font-bold text-lg">{fmtMoney(record.total_sales)}</span>
              <span className="text-white/50 text-xs mt-0.5">{p('deals_count', { count: record.closed_deals })}</span>
            </div>
          </td>
        );
      case 'conversion_rate':
        return <td key={col.key} className="px-6 py-4"><span className="text-white font-semibold">{record.conversion_rate}%</span></td>;
      case 'performance':
        return <td key={col.key} className="px-6 py-4 text-center"><div className="flex justify-center">{getPerformanceBadge(record.conversion_rate)}</div></td>;
      case 'status':
        return (
          <td key={col.key} className="px-6 py-4 text-center">
            <div className="flex justify-center">
              <QawaemClickableStatusBadge
                status={record.status}
                recordId={record.id}
                options={statusOptions}
                onChange={handleStatusChange}
                title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
              />
            </div>
          </td>
        );
      default:
        return null;
    }
  };

  return (
    <>
    <QawaemErpListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={UserCheck}
      stats={listPageStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{t('common.delete')} ({selectedRecords.length})</span>
        </button>
      ) : null}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      onCreate={openNewAgentModal}
      createLabel={p('add_agent')}
      columns={qawaemColumnDefs}
      visibleColumnCount={qawaemVisibleColumnCount}
      totalColumnCount={qawaemColumnDefs.length}
      headerActions={(
        <>
          {!qawaem ? (
            <button
              type="button"
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-2 rounded-xl font-semibold flex items-center gap-2`}
            >
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">{p('advanced_options')}</span>
            </button>
          ) : null}
          <button type="button" onClick={loadAgents} className="refresh-button" title={p('refresh')}>
            <RefreshCcw size={20} />
          </button>
        </>
      )}
      importExport={buildListImportExportProps({
        showImportDropdown,
        setShowImportDropdown,
        showExportDropdown,
        setShowExportDropdown,
        importDropdownRef,
        exportDropdownRef,
        importLabel: p('import'),
        exportLabel: p('export'),
        generatingLabel: p('generating'),
        isGeneratingPDF,
      })}
    >
      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_agents' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.name} onClick={() => { const fid = getDistributionFilterId(item); setActiveDistributionFilter(activeDistributionFilter === fid ? null : fid); }} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === getDistributionFilterId(item) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === getDistributionFilterId(item) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === getDistributionFilterId(item) ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {visibleColumns.map((col) => renderAgentHeader(col))}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredAgents.length > 0 ? (
                  pagedFilteredAgents.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                      />
                      {visibleColumns.map((col) => renderAgentCell(record, col))}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><UserCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_state')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm flex-wrap gap-2">
                      <span>{p('footer_total_agents', { count: filteredAgents.length })}</span>
                      <span>{p('footer_total_sales')} <span className="text-white font-semibold">{fmtMoney(filteredAgents.reduce((sum, a) => sum + (parseFloat(a.total_sales) || 0), 0))}</span></span>
                      <span>{p('footer_commissions')} <span className="text-white font-semibold">{fmtMoney(filteredAgents.reduce((sum, a) => sum + (parseFloat(a.total_commission) || 0), 0))}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredAgents.length > 0 ? (
              pagedFilteredAgents.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><UserCheck size={20} className="text-emerald-400" />{record.first_name} {record.last_name}</h3>
                        <div className="flex gap-2">
                          <QawaemClickableStatusBadge
                            status={record.status}
                            recordId={record.id}
                            options={statusOptions}
                            onChange={handleStatusChange}
                            title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                          {getPerformanceBadge(record.conversion_rate)}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_agent_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_code')}</span> {record.code}</div>
                            <div className="flex items-center gap-2"><Mail size={14} className="text-blue-400 shrink-0" /><span className="font-medium">{p('label_email')}</span> {record.email || p('dash')}</div>
                            <div className="flex items-center gap-2"><Phone size={14} className="text-green-400 shrink-0" /><span className="font-medium">{p('label_phone')}</span> {record.phone ? (<a href={getCrmPhoneTelHref(record.phone)} className="text-green-400 hover:underline">{formatCrmPhone(record.phone)}</a>) : p('dash')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_territory_quota')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2"><MapPin size={14} className="text-amber-400 shrink-0" /><span className="font-medium">{p('label_territory')}</span> {getRegionLabel(record.territory) || record.territory || p('dash')}</div>
                            <div><span className="font-medium">{p('label_region')}</span> {getRegionLabel(record.region) || record.region || p('dash')}</div>
                            <div><span className="font-medium">{p('label_quota')}</span> {fmtMoney(record.sales_quota)}</div>
                            <div><span className="font-medium">{p('label_commission_rate')}</span> {record.commission_rate}%</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_performance')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_total_sales')}</span> <span className="text-white font-bold text-lg">{fmtMoney(record.total_sales)}</span></div>
                            <div><span className="font-medium">{p('label_active_leads')}</span> {record.active_leads}</div>
                            <div><span className="font-medium">{p('label_closed_deals')}</span> {record.closed_deals}</div>
                            <div><span className="font-medium">{p('label_conversion_rate')}</span> {record.conversion_rate}%</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_commissions_status')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium text-green-400">{p('label_commission')}</span> <span className="text-white font-bold">{fmtMoney(record.total_commission)}</span></div>
                            <div className="pt-2">
                              <QawaemClickableStatusBadge
                                status={record.status}
                                recordId={record.id}
                                options={statusOptions}
                                onChange={handleStatusChange}
                                title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                              />
                            </div>
                            <div className="pt-1">{getPerformanceBadge(record.conversion_rate)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      className="ml-4"
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><UserCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_state')}</p></div>
            )}
          </div>
        )}
    </QawaemErpListPageShell>

      {showViewModal && viewingRecord && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => { setShowViewModal(false); setViewingRecord(null); }}
          title={p('view_title')}
          icon={Eye}
          maxWidth="1100px"
          testId="crm-sales-agents-view-modal"
        >
          <div className="space-y-4">
            <div className={viewPanel}>
              <h3 className={`font-semibold mb-4 flex items-center gap-2 ${cfgTheme.title}`}><UserCheck size={18} className="text-emerald-400" />{p('view_general_info')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><p className={viewLabel}>{p('view_code')}</p><p className={viewValue}>{viewingRecord.code}</p></div>
                <div><p className={viewLabel}>{p('view_first_name')}</p><p className={viewValue}>{viewingRecord.first_name}</p></div>
                <div><p className={viewLabel}>{p('view_last_name')}</p><p className={viewValue}>{viewingRecord.last_name}</p></div>
                <div><p className={viewLabel}>{p('view_email')}</p><p className={viewValue}>{viewingRecord.email}</p></div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={viewPanel}>
                <h3 className={`font-semibold mb-4 ${cfgTheme.title}`}>{p('view_territory_quota')}</h3>
                <div className="space-y-3">
                  <div><p className={viewLabel}>{p('view_territory')}</p><p className={viewValue}>{viewingRecord.territory}</p></div>
                  <div><p className={viewLabel}>{p('view_region')}</p><p className={viewValue}>{viewingRecord.region}</p></div>
                  <div><p className={viewLabel}>{p('view_sales_quota')}</p><p className={viewValue}>{fmtMoney(viewingRecord.sales_quota)}</p></div>
                  <div><p className={viewLabel}>{p('view_commission_rate')}</p><p className={viewValue}>{viewingRecord.commission_rate}%</p></div>
                  <div><p className={viewLabel}>{p('view_hire_date')}</p><p className={viewValue}>{fmtDate(viewingRecord.hire_date)}</p></div>
                </div>
              </div>
              <div className={viewPanel}>
                <h3 className={`font-semibold mb-4 ${cfgTheme.title}`}>{p('view_performance_commissions')}</h3>
                <div className="space-y-3">
                  <div><p className={viewLabel}>{p('col_total_sales')}</p><p className={`font-bold text-xl ${cfgTheme.title}`}>{fmtMoney(viewingRecord.total_sales)}</p></div>
                  <div><p className={viewLabel}>{p('view_active_leads')}</p><p className={viewValue}>{viewingRecord.active_leads}</p></div>
                  <div><p className={viewLabel}>{p('view_closed_deals')}</p><p className={viewValue}>{viewingRecord.closed_deals}</p></div>
                  <div><p className={viewLabel}>{p('view_conversion_rate')}</p><p className={viewValue}>{viewingRecord.conversion_rate}%</p></div>
                  <div><p className={viewLabel}>{p('view_total_commission')}</p><p className="font-bold text-xl text-green-400">{fmtMoney(viewingRecord.total_commission)}</p></div>
                </div>
              </div>
            </div>
            {viewingRecord.notes && (
              <div className={viewPanel}>
                <h3 className={`font-semibold mb-4 ${cfgTheme.title}`}>{p('view_notes')}</h3>
                <p className={cfgTheme.subtitle}>{viewingRecord.notes}</p>
              </div>
            )}
          </div>
        </QawaemErpViewModal>
      )}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('modal_edit_title') : p('modal_new_title')}
          icon={UserCheck}
          formId={AGENT_FORM_ID}
          saveLabel={t('common.save')}
          onSubmit
          maxWidth="1200px"
          testId="crm-sales-agents-modal"
        >
          <CrmModalFormBody>
            <CrmModalFormTabs
              tabs={agentFormTabs}
              activeTab={formTab}
              onTabChange={setFormTab}
              ariaLabel={editingRecord ? p('modal_edit_title') : p('modal_new_title')}
            />
            <CrmModalFormPanel>
              <form id={AGENT_FORM_ID} onSubmit={handleSubmit}>
                {formTab === 'agent' && (
                  <CrmModalFieldGrid>
                    <div><label className={cfgTheme.label}><RequiredLabelText text={p('label_code')} required /></label><input type="text" required value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} readOnly={!editingRecord} placeholder={p('placeholder_code')} className={cfgTheme.input} /></div>
                    <div><label className={cfgTheme.label}><RequiredLabelText text={p('label_first_name_required')} /></label><input type="text" required value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder={p('placeholder_first_name')} className={cfgTheme.input} /></div>
                    <div><label className={cfgTheme.label}><RequiredLabelText text={p('label_last_name_required')} /></label><input type="text" required value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} placeholder={p('placeholder_last_name')} className={cfgTheme.input} /></div>
                    <div><label className={cfgTheme.label}>{p('label_email_field')}</label><input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={p('placeholder_email')} className={cfgTheme.input} /></div>
                    <div><label className={cfgTheme.label}>{p('label_phone_field')}</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder={phonePlaceholder} className={cfgTheme.input} /></div>
                    <div><label className={cfgTheme.label}>{p('label_mobile')}</label><input type="tel" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} placeholder={phonePlaceholder} className={cfgTheme.input} /></div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_status')}</label>
                      <CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={AGENT_STATUS_OPTIONS} placeholder={p('placeholder_status')} searchable={true} creatable={true} autoSelect={false} />
                    </div>
                    <div><label className={cfgTheme.label}>{p('label_address')}</label><input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder={p('placeholder_address')} className={cfgTheme.input} /></div>
                    <div><label className={cfgTheme.label}>{p('label_city')}</label><input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder={p('placeholder_city')} className={cfgTheme.input} /></div>
                  </CrmModalFieldGrid>
                )}
                {formTab === 'territory' && (
                  <CrmModalFieldGrid>
                    <div>
                      <label className={cfgTheme.label}>{p('label_territory_field')}</label>
                      <CustomSelect value={formData.territory} onChange={(value) => setFormData({ ...formData, territory: value })} options={regionOptions} placeholder={p('placeholder_territory')} searchable={true} />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_region_field')}</label>
                      <CustomSelect value={formData.region} onChange={(value) => setFormData({ ...formData, region: value })} options={regionOptions} placeholder={p('placeholder_region')} searchable={true} />
                    </div>
                    <div><label className={cfgTheme.label}>{p('label_company')}</label><input type="text" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} placeholder={p('placeholder_company')} className={cfgTheme.input} /></div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_hire_date')}</label>
                      <CustomDatePicker value={formData.hire_date} onChange={(value) => setFormData({ ...formData, hire_date: value })} placeholder={p('placeholder_date')} showTime={false} />
                    </div>
                    <div><label className={cfgTheme.label}>{p('label_sales_quota')}</label><input type="number" step="0.01" value={formData.sales_quota} onChange={(e) => setFormData({ ...formData, sales_quota: e.target.value })} placeholder={p('placeholder_sales_quota')} className={cfgTheme.input} /></div>
                    <div><label className={cfgTheme.label}>{p('label_commission_rate_pct')}</label><input type="number" step="0.01" value={formData.commission_rate} onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })} placeholder={p('placeholder_commission_rate')} className={cfgTheme.input} /></div>
                  </CrmModalFieldGrid>
                )}
                {formTab === 'performance' && (
                  <CrmModalFieldGrid>
                    <div><label className={cfgTheme.label}>{p('label_total_sales_amount')}</label><input type="number" step="0.01" value={formData.total_sales} onChange={(e) => setFormData({ ...formData, total_sales: e.target.value })} placeholder={p('placeholder_total_sales')} className={cfgTheme.input} /></div>
                    <div><label className={cfgTheme.label}>{p('label_active_leads_field')}</label><input type="number" value={formData.active_leads} onChange={(e) => setFormData({ ...formData, active_leads: e.target.value })} placeholder={p('placeholder_active_leads')} className={cfgTheme.input} /></div>
                    <div><label className={cfgTheme.label}>{p('label_closed_deals_field')}</label><input type="number" value={formData.closed_deals} onChange={(e) => setFormData({ ...formData, closed_deals: e.target.value })} placeholder={p('placeholder_closed_deals')} className={cfgTheme.input} /></div>
                    <div><label className={cfgTheme.label}>{p('label_conversion_rate_pct')}</label><input type="number" step="0.01" value={formData.conversion_rate} onChange={(e) => setFormData({ ...formData, conversion_rate: e.target.value })} placeholder={p('placeholder_conversion_rate')} className={cfgTheme.input} /></div>
                    <div><label className={cfgTheme.label}>{p('label_total_commission_amount')}</label><input type="number" step="0.01" value={formData.total_commission} onChange={(e) => setFormData({ ...formData, total_commission: e.target.value })} placeholder={p('placeholder_total_commission')} className={cfgTheme.input} /></div>
                    <div className="md:col-span-3">
                      <label className={cfgTheme.label}>{p('label_notes_field')}</label>
                      <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="4" placeholder={p('placeholder_notes')} className={`${cfgTheme.input} resize-none`} />
                    </div>
                  </CrmModalFieldGrid>
                )}
              </form>
            </CrmModalFormPanel>
          </CrmModalFormBody>
        </QawaemErpCrudModal>
      )}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default SalesAgentsPage;

